import { useEffect, useRef, useState } from 'react';

export type LoadImageResult = {
  src?: string;
  status: 'loaded' | 'pending' | 'failed';
  width?: number;
  height?: number;
};

export function useLoadImage(
  src?: string,
  onLoad?: (event?: Event, size?: Size) => void,
  onError?: () => void
): LoadImageResult {
  const onLoadRef = useRef(onLoad);
  const onErrorRef = useRef(onError);

  const [state, setState] = useState<Omit<LoadImageResult, 'src'>>({
    status: 'pending',
    width: undefined,
    height: undefined
  });

  useEffect(() => {
    if (!src) {
      console.warn('"src" expected');
      return;
    }

    const image = new Image();
    image.src = src;

    setState({ status: 'pending' });

    const handleLoad = () => {
      const width = image.naturalWidth;
      const height = image.naturalHeight;

      setState({ status: 'loaded', width, height });
      onLoadRef.current && onLoadRef.current(undefined, { width, height });
    };

    image.addEventListener('load', handleLoad);

    const handleError = () => {
      setState({ status: 'failed' });
      onErrorRef.current && onErrorRef.current();
    };

    image.addEventListener('error', handleError);

    return () => {
      image.removeEventListener('load', handleLoad);
      image.removeEventListener('error', handleError);
    };
  }, [src]);

  return { src, ...state };
}

interface Size {
  width?: number;
  height?: number;
}
